import { action, extendObservable, computed } from 'mobx';
import { StatusCodes } from 'http-status-codes';

import { translate } from 'services/i18nService';
import MessagingService from 'services/MessagingService/MessagingService';
import {
  ADD_BROADCASTER_TO_SPONSORED_TAG,
  ADD_BROADCASTER_TO_SPONSORED_TAG_ERROR,
  ADD_BROADCASTER_TO_SPONSORED_TAG_RESPONSE,
  ADD_STUDIO_BROADCASTERS_TO_SPONSORED_TAG,
  ADD_STUDIO_BROADCASTERS_TO_SPONSORED_TAG_ERROR,
  ADD_STUDIO_BROADCASTERS_TO_SPONSORED_TAG_RESPONSE,
  CHANGE_SPONSORED_TAG_SWITCH_STATUS,
  CHANGE_SPONSORED_TAG_SWITCH_STATUS_ERROR,
  CHANGE_SPONSORED_TAG_SWITCH_STATUS_RESPONSE,
  CREATE_SPONSORED_TAG,
  CREATE_SPONSORED_TAG_ERROR,
  CREATE_SPONSORED_TAG_RESPONSE,
  DELETE_SPONSORED_TAG,
  DELETE_SPONSORED_TAG_ERROR,
  DELETE_SPONSORED_TAG_RESPONSE,
  GET_BROADCASTERS_LIST_FOR_TAG,
  GET_BROADCASTERS_LIST_FOR_TAG_RESPONSE,
  GET_SPONSORED_TAGS_LIST,
  GET_SPONSORED_TAGS_LIST_RESPONSE,
  PREDEFINED_TAGS_RESPONSE,
  REMOVE_BROADCASTER_USING_SPONSORED_TAG,
  REMOVE_BROADCASTER_USING_SPONSORED_TAG_ERROR,
  REMOVE_BROADCASTER_USING_SPONSORED_TAG_RESPONSE,
  SET_TAGS_FROM_BROADCASTER_SETTINGS_AND_GET_PREDEFINED_TAGS,
} from 'services/MessagingService/EventNames';
import NotificationService from 'services/NotificationService';
import TagsService from 'services/TagsService';
import tagsStoreInitialState from 'stores/TagsStoreInitialState';
import { REGEX_CLEAR_PASSWORD_CHARACTERS_TAGS } from 'enum/helpers';
import { isStringHTMLTag } from 'utils/helpers';


class TagsStore {
  constructor() {
    extendObservable(this, tagsStoreInitialState);
    this.tagService = TagsService;
    this.messagingService = MessagingService();
    this.messagingService.on(GET_SPONSORED_TAGS_LIST_RESPONSE, this._handleReceivedTagsList);
    this.messagingService.on(GET_BROADCASTERS_LIST_FOR_TAG_RESPONSE, this._handleReceivedBroadcastersList);
    this.messagingService.on(CHANGE_SPONSORED_TAG_SWITCH_STATUS_RESPONSE, this._handleSwitchStatusChanged);
    this.messagingService.on(CHANGE_SPONSORED_TAG_SWITCH_STATUS_ERROR, this._handleShowErrorNotification);
    this.messagingService.on(CREATE_SPONSORED_TAG_RESPONSE, this._handleNewTagCreated);
    this.messagingService.on(CREATE_SPONSORED_TAG_ERROR, this._handleShowErrorNotification);
    this.messagingService.on(REMOVE_BROADCASTER_USING_SPONSORED_TAG_RESPONSE, this._handleBroadcasterRemovedFromList);
    this.messagingService.on(REMOVE_BROADCASTER_USING_SPONSORED_TAG_ERROR, this._handleShowErrorNotification);
    this.messagingService.on(ADD_BROADCASTER_TO_SPONSORED_TAG_RESPONSE, this._handleBroadcasterAddedToList);
    this.messagingService.on(ADD_BROADCASTER_TO_SPONSORED_TAG_ERROR, this._handleShowErrorNotification);
    this.messagingService.on(ADD_STUDIO_BROADCASTERS_TO_SPONSORED_TAG_RESPONSE, this._handleBroadcasterAddedToList);
    this.messagingService.on(ADD_STUDIO_BROADCASTERS_TO_SPONSORED_TAG_ERROR, this._handleShowErrorNotification);
    this.messagingService.on(DELETE_SPONSORED_TAG_RESPONSE, this._handleDeleteTag);
    this.messagingService.on(DELETE_SPONSORED_TAG_ERROR, this._handleShowErrorNotification);
    this.messagingService.on(PREDEFINED_TAGS_RESPONSE, this.setPredefinedTags);
    this.messagingService.on(
      SET_TAGS_FROM_BROADCASTER_SETTINGS_AND_GET_PREDEFINED_TAGS,
      this.setTagsFromBroadcasterSettingsAndGetPredefinedTags
    );
  }

  destructor() {
    this.messagingService.off(GET_SPONSORED_TAGS_LIST_RESPONSE, this._handleReceivedTagsList);
    this.messagingService.off(GET_BROADCASTERS_LIST_FOR_TAG_RESPONSE, this._handleReceivedBroadcastersList);
    this.messagingService.off(CHANGE_SPONSORED_TAG_SWITCH_STATUS_RESPONSE, this._handleSwitchStatusChanged);
    this.messagingService.off(CHANGE_SPONSORED_TAG_SWITCH_STATUS_ERROR, this._handleShowErrorNotification);
    this.messagingService.off(CREATE_SPONSORED_TAG_RESPONSE, this._handleNewTagCreated);
    this.messagingService.off(CREATE_SPONSORED_TAG_ERROR, this._handleShowErrorNotification);
    this.messagingService.off(REMOVE_BROADCASTER_USING_SPONSORED_TAG_RESPONSE, this._handleBroadcasterRemovedFromList);
    this.messagingService.off(REMOVE_BROADCASTER_USING_SPONSORED_TAG_ERROR, this._handleShowErrorNotification);
    this.messagingService.off(ADD_BROADCASTER_TO_SPONSORED_TAG_RESPONSE, this._handleBroadcasterAddedToList);
    this.messagingService.off(ADD_BROADCASTER_TO_SPONSORED_TAG_ERROR, this._handleShowErrorNotification);
    this.messagingService.off(ADD_STUDIO_BROADCASTERS_TO_SPONSORED_TAG_RESPONSE, this._handleBroadcasterAddedToList);
    this.messagingService.off(ADD_STUDIO_BROADCASTERS_TO_SPONSORED_TAG_ERROR, this._handleShowErrorNotification);
    this.messagingService.off(DELETE_SPONSORED_TAG_RESPONSE, this._handleDeleteTag);
    this.messagingService.off(DELETE_SPONSORED_TAG_ERROR, this._handleShowErrorNotification);
    this.messagingService.off(PREDEFINED_TAGS_RESPONSE, this.setPredefinedTags);
    this.messagingService.off(
      SET_TAGS_FROM_BROADCASTER_SETTINGS_AND_GET_PREDEFINED_TAGS,
      this.setTagsFromBroadcasterSettingsAndGetPredefinedTags
    );
  }

  @action setShowTagsList = (val) => {
    this.showTagsList = val;
  };

  @action setSelectedTag = (tag, id) => {
    this.selectedTag = tag;
    this.tagId = id;
  };

  @action getSponsoredTagsList = () => {
    this.messagingService.emit(GET_SPONSORED_TAGS_LIST, { offset: 0, direction: 'DESC' });
  };

  @action getSponsoredTagsListPerPage = (offset) => {
    this.offsetSponsoredTags = offset;
    this.messagingService.emit(GET_SPONSORED_TAGS_LIST, { offset, direction: 'DESC' });
  };

  @action getBroadcastersListForTag = () => {
    this.messagingService.emit(GET_BROADCASTERS_LIST_FOR_TAG, { tagId: this.tagId, offset: 0, direction: 'DESC' });
  };

  @action getBroadcastersListPerPage = (offset) => {
    this.offsetBroadcasters = offset;
    this.messagingService.emit(GET_BROADCASTERS_LIST_FOR_TAG, { tagId: this.tagId, offset, direction: 'DESC' });
  };

  @action changeSwitchValue = (checked, ind, type) => {
    this.switchValueSaved = true;
    this.switchValueIndex = ind;
    this.switchValueType = type;
    this.switchValueOriginal = !checked;
    this.messagingService.emit(CHANGE_SPONSORED_TAG_SWITCH_STATUS, {
      id: this.sponsoredTags[ind].id,
      active: type ? checked : this.sponsoredTags[ind].active,
      exclusive: !type ? checked : this.sponsoredTags[ind].exclusive,
    });
  };

  @action addNewTag = (tagName) => {
    this.messagingService.emit(CREATE_SPONSORED_TAG, tagName);
  };

  @action removeBroadcaster = (broadcasterName) => {
    this.messagingService.emit(REMOVE_BROADCASTER_USING_SPONSORED_TAG, {
      tagId: this.tagId,
      username: broadcasterName,
    });
  };

  @action addBroadcasterToTag = (broadcasterName) => {
    this.messagingService.emit(ADD_BROADCASTER_TO_SPONSORED_TAG, {
      tagId: this.tagId,
      username: broadcasterName,
      studio: false,
    });
  };

  @action addStudioBroadcastersToTag = (studioName) => {
    this.messagingService.emit(ADD_STUDIO_BROADCASTERS_TO_SPONSORED_TAG, {
      tagId: this.tagId,
      username: studioName,
      studio: true,
    });
  };

  @action deleteTag = () => {
    this.messagingService.emit(DELETE_SPONSORED_TAG, this.tagId);
  };

  @action _handleDeleteTag = () => {
    if (this.sponsoredTags.length === 1 && this.offsetSponsoredTags > 0) {
      this.offsetSponsoredTags -= 10;
    }
    this.messagingService.emit(GET_SPONSORED_TAGS_LIST, { offset: this.offsetSponsoredTags, direction: 'DESC' });
    this.showNotification('Sponsored Tag Deleted', 'SUCCESS');
    this.setShowTagsList(true);
  };

  @action _handleReceivedTagsList = (data) => {
    this.sponsoredTags = data.tags;
    this.totalCountSponsoredTags = data.totalCount;
  };

  @action _handleReceivedBroadcastersList = (data) => {
    this.broadcasters = data.tagUsers;
    if (data.isStudio) {
      const addedCount = data.totalCount - this.totalCountBroadcasters;
      if (addedCount === 0) {
        this.showNotification('No Broadcasters to add', 'SUCCESS');
      } else {
        this.showNotification(`${addedCount} Broadcasters Added`, 'SUCCESS');
      }
    }
    this.totalCountBroadcasters = data.totalCount;
  };

  @action _handleBroadcasterAddedToList = (isStudio) => {
    this.offsetBroadcasters = 0;
    this.messagingService.emit(GET_BROADCASTERS_LIST_FOR_TAG, {
      tagId: this.tagId,
      offset: this.offsetBroadcasters,
      direction: 'DESC',
      isStudio,
    });
    if (!isStudio) {
      this.showNotification('Broadcaster Added', 'SUCCESS');
    }
  };

  @action _handleBroadcasterRemovedFromList = () => {
    if (this.broadcasters.length === 1 && this.offsetBroadcasters > 0) {
      this.offsetBroadcasters -= 20;
    }
    this.messagingService.emit(GET_BROADCASTERS_LIST_FOR_TAG, {
      tagId: this.tagId,
      offset: this.offsetBroadcasters,
      direction: 'DESC',
    });
    this.showNotification('Broadcaster Removed', 'SUCCESS');
  };

  @action _handleSwitchStatusChanged = () => {
    this.switchValueSaved = true;
    this.showNotification(translate('account.changesSaved'), 'SUCCESS');
  };

  @action _handleNewTagCreated = () => {
    this.offsetSponsoredTags = 0;
    this.messagingService.emit(GET_SPONSORED_TAGS_LIST, { offset: 0, direction: 'DESC' });
    this.showNotification('New Sponsored Tag Added', 'SUCCESS');
  };

  _showErrorMessage = (code, message) => {
    switch (code) {
      case 'user_is_studio':
        return 'User is a Studio. Use ‘Add Studio’ Button';
      case 'user_not_found':
        return 'Username not found';
      default:
        return message;
    }
  };

  // eslint-disable-next-line complexity
  @action _handleShowErrorNotification = (data) => {
    switch (data.type) {
      case 'ADD_BROADCASTER':
        if (data.status) {
          switch (data.status) {
            case StatusCodes.BAD_REQUEST:
              this.showNotification(this._showErrorMessage(data.code, data.message), 'ERROR');
              break;
            case StatusCodes.INTERNAL_SERVER_ERROR:
              this.showNotification('Broadcaster already added', 'ERROR');
              break;
            default:
              this.showNotification('Something went wrong', 'ERROR');
          }
        } else {
          this.showNotification('Something went wrong', 'ERROR');
        }
        break;
      case 'ADD_STUDIO_BROADCASTERS':
        if (data.status) {
          switch (data.status) {
            case StatusCodes.BAD_REQUEST:
              if (data.code === 'empty_studio') {
                this.showNotification('No Broadcasters to add', 'SUCCESS');
              } else {
                this.showNotification(this._showErrorMessage(data.code, data.message), 'ERROR');
              }
              break;
            case StatusCodes.INTERNAL_SERVER_ERROR:
              this.showNotification('No Broadcasters to add', 'SUCCESS');
              break;
            default:
              this.showNotification('Something went wrong', 'ERROR');
          }
        } else {
          this.showNotification('Something went wrong', 'ERROR');
        }
        break;
      case 'TAG_SWITCH_VALUE':
        this.switchValueSaved = false;
        if (data.status && typeof data.status !== 'undefined') {
          switch (data.status.status) {
            case StatusCodes.BAD_REQUEST:
              this.showNotification('Action not allowed', 'ERROR');
              break;
            case StatusCodes.INTERNAL_SERVER_ERROR:
              this.showNotification('Internal Server Error', 'ERROR');
              break;
            default:
              this.showNotification('Something went wrong', 'ERROR');
          }
        } else {
          this.showNotification('Something went wrong', 'ERROR');
        }
        break;
      case 'CREATE_TAG':
      case 'REMOVE_BROADCASTER_FROM_TAG':
      case 'DELETE_TAG':
        if (data.status && typeof data.status !== 'undefined') {
          switch (data.status.status) {
            case StatusCodes.BAD_REQUEST:
              if (data.type === 'CREATE_TAG') {
                this.showNotification('Tag already exists', 'ERROR');
              } else {
                this.showNotification('Action not allowed', 'ERROR');
              }
              break;
            case StatusCodes.INTERNAL_SERVER_ERROR:
              this.showNotification('Internal Server Error', 'ERROR');
              break;
            default:
              this.showNotification('Something went wrong', 'ERROR');
          }
        } else {
          this.showNotification('Something went wrong', 'ERROR');
        }
        break;
      default:
        this.showNotification('Something went wrong', 'ERROR');
    }
  };

  showNotification = (text, messageType) => {
    NotificationService.create({
      type: NotificationService.type.TOAST,
      text,
      subtype: NotificationService.subtype[messageType],
    });
  };

  @action openPredefinedTagsAndSetAnchor = (event) => {
    this.anchorPredefinedTags = this.anchorPredefinedTags ? null : event.currentTarget;
    this.isPredefinedTagVisible = Boolean(this.anchorPredefinedTags);
  };

  @action setTagsFromBroadcasterSettingsAndGetPredefinedTags = ({ tags, username, accessHash }) => {
    this.chosenTagsFromBroadcasterSettings = tags;
    TagsService.getPredefinedTags(username, accessHash);
  };

  @action setPredefinedTags = (tags) => {
    this.predefinedTags = tags;
    const chosenTags = this.chosenTagsFromBroadcasterSettings.map((item) => {
      const predefinedTag = tags.find((tag) => tag.name === item);
      if (predefinedTag) {
        return { ...predefinedTag, isActive: true };
      }

      return {
        name: item,
        customTag: true,
        // eslint-disable-next-line no-magic-numbers
        id: Math.floor(Math.random() * (100 - 50 + 1)) + 50,
      };

    });
    this.chosenTags = chosenTags;
  };

  @action closePedefinedTagsPopper = () => {
    this.anchorPredefinedTags = null;
    this.isPredefinedTagVisible = false;
  };

  @action setChosenTags = (tag) => {
    const isTagAlreadyChosen = this.chosenTags.some((oneTag) => oneTag.name === tag.name);
    if (isTagAlreadyChosen && !tag.customTag) {
      this.removeChosenTag(tag);
    } else if (this.chosenTags.length < 7 && !isTagAlreadyChosen) {
      this.chosenTags = [tag, ...this.chosenTags];
    }
    this.searchPredefinedTagValue = '';
  };

  @action removeChosenTag = (tag) => {
    this.chosenTags = this.chosenTags.filter((oneTag) => tag.name.toLowerCase() !== oneTag.name.toLowerCase());
  };

  @action setSearchPredefinedTagValue = (event) => {
    const PREDEFINED_TAG_MAX_LENGTH = 15;
    if (event.target.value.length < PREDEFINED_TAG_MAX_LENGTH && !isStringHTMLTag(event.target.value)) {
      if (event.target.value.length === 1 && event.target.value.match(' ')) {
        return;

      }
      const result = event.target.value.replace(REGEX_CLEAR_PASSWORD_CHARACTERS_TAGS, '');
      this.searchPredefinedTagValue = result;
    }
  };

  @action clearSearchPredefinedTagValue = () => {
    this.searchPredefinedTagValue = '';
  };

  @computed get filteredPredefinedTags() {
    const activeElements = this.predefinedTags.map((tag) => {
      if (this.chosenTags.some((oneTag) => oneTag.name === tag.name)) {
        return { ...tag, isActive: true };
      }

      return tag;

    });
    const filteredTags = activeElements.filter((tag) =>
      tag.name.toLowerCase().startsWith(this.searchPredefinedTagValue.toLowerCase())
    );
    const isFilterApplied = this.searchPredefinedTagValue !== '';

    return isFilterApplied ? filteredTags : activeElements;
  }

  /**
   * Method for updating broadcast tags
   * @param {string} username
   * @param {string} accessHash
   * @param {string} tags
   */
  updatePredefinedTags = (username, accessHash, tags) => {
    TagsService.updatePredefinedTags(username, accessHash, tags);
  };

  /* @method getter statusTagsMessage - making string from chosen tags
   * @returns string
   */
  @computed get statusTagsMessage() {
    const tagsString = this.chosenTags.map((tag) => ` #${tag.name}`).join('');

    return tagsString;
  }
}

export default new TagsStore();
